/**
 * Phase 167 — Weekly Scout Heatmap Email Digest Control Panel
 *
 * Mounted at the top of the Super Admin Video Analysis page. Surfaces the
 * weekly digest cron health + lets ops manually trigger / preview the
 * Monday 09:00 IST blast. Talks to:
 *   GET    /admin/scouting/digest/stats
 *   GET    /admin/scouting/digest/schedule
 *   PUT    /admin/scouting/digest/schedule
 *   GET    /admin/scouting/digest/preview
 *   POST   /admin/scouting/digest/run
 */
import { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Mail, Send, Eye, RefreshCw, Loader2, Inbox, CheckCircle2, AlertTriangle, Clock,
  MailOpen, MousePointerClick, Trophy,
} from 'lucide-react';
import { toast } from 'sonner';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export default function Phase167DigestPanel() {
  const [stats, setStats] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showRun, setShowRun] = useState(false);
  const [running, setRunning] = useState(false);
  const [runOverride, setRunOverride] = useState('');

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, sch, a] = await Promise.all([
        api.get('/admin/scouting/digest/stats').catch(() => ({ data: null })),
        api.get('/admin/scouting/digest/schedule').catch(() => ({ data: null })),
        api.get('/admin/scouting/digest/audit?limit=10').catch(() => ({ data: null })),
      ]);
      setStats(s.data);
      setSchedule(sch.data);
      setAudit(a.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const openPreview = async () => {
    setShowPreview(true);
    setPreviewLoading(true);
    setPreview(null);
    try {
      const r = await api.get('/admin/scouting/digest/preview');
      setPreview(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Preview unavailable');
      setShowPreview(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const toggleSchedule = async (enabled) => {
    try {
      const r = await api.put('/admin/scouting/digest/schedule', {
        enabled,
        weekday: schedule?.weekday ?? 0,
        hour_ist: schedule?.hour_ist ?? 9,
      });
      setSchedule((s) => ({ ...(s || {}), ...r.data, next_window_label: `${WEEKDAYS[r.data.weekday]} ${String(r.data.hour_ist).padStart(2, '0')}:00 IST` }));
      toast.success(enabled ? 'Weekly digest cron enabled' : 'Weekly digest cron disabled');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not update schedule');
    }
  };

  const setScheduleField = async (field, value) => {
    const next = { ...(schedule || { enabled: true, weekday: 0, hour_ist: 9 }), [field]: value };
    try {
      const r = await api.put('/admin/scouting/digest/schedule', {
        enabled: !!next.enabled,
        weekday: Number(next.weekday),
        hour_ist: Number(next.hour_ist),
      });
      setSchedule((s) => ({ ...(s || {}), ...r.data, next_window_label: `${WEEKDAYS[r.data.weekday]} ${String(r.data.hour_ist).padStart(2, '0')}:00 IST` }));
      toast.success('Schedule updated');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Schedule update failed');
    }
  };

  const runNow = async (dryRun) => {
    setRunning(true);
    try {
      const body = { dry_run: !!dryRun };
      if (runOverride.trim()) body.override_recipient = runOverride.trim();
      const r = await api.post('/admin/scouting/digest/run', body);
      if (r.data?.success) {
        toast.success(
          dryRun
            ? `Dry-run queued for ${r.data.queued ?? 1} academ${(r.data.queued ?? 1) === 1 ? 'y' : 'ies'}`
            : `Live blast queued for ${r.data.queued ?? 1} academ${(r.data.queued ?? 1) === 1 ? 'y' : 'ies'}`
        );
        setShowRun(false);
        setRunOverride('');
        loadAll();
      } else {
        toast.warning(r.data?.message || 'No eligible academies');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Run failed');
    } finally {
      setRunning(false);
    }
  };

  if (loading && !stats && !schedule) {
    return (
      <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50/40 via-white to-teal-50/30">
        <CardContent className="p-5 flex items-center gap-3 text-emerald-700">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading Phase 167 digest panel…
        </CardContent>
      </Card>
    );
  }

  const eligible = stats?.eligible_now ?? 0;
  const sentWeek = stats?.sent_this_week ?? 0;
  const total = stats?.total_all_time_sent ?? 0;
  const failed = stats?.failed_all_time ?? 0;
  const enabled = !!schedule?.enabled;
  const last = stats?.last_campaign;

  return (
    <Card
      className="border-emerald-200 bg-gradient-to-br from-emerald-50/40 via-white to-teal-50/30"
      data-testid="phase167-digest-panel"
    >
      <CardContent className="p-5 sm:p-6 space-y-5">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <Mail className="h-5 w-5 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="font-display font-bold text-base sm:text-lg text-slate-900" data-testid="phase167-title">
                  Weekly Scout Heatmap Digest
                </h2>
                <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[9px] font-bold uppercase tracking-widest">Phase 167</Badge>
                <Badge className="bg-purple-100 text-purple-800 border-purple-200 text-[9px] font-bold uppercase tracking-widest">Legend Tier</Badge>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                Monday 09:00 IST · "Your Week in Scouting" email to every active Legend-tier academy owner.
              </p>
            </div>
          </div>
          <Button
            variant="outline" size="sm" onClick={loadAll}
            disabled={loading}
            className="h-8 text-xs"
            data-testid="phase167-refresh"
          >
            {loading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
            Refresh
          </Button>
        </div>

        {/* Stat tiles */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="bg-white rounded-xl border border-emerald-200 p-3.5"
            data-testid="phase167-stat-eligible">
            <div className="text-[10px] font-black uppercase tracking-widest text-emerald-700 flex items-center gap-1">
              <Inbox className="h-3 w-3" /> Eligible Now
            </div>
            <div className="text-2xl font-extrabold text-slate-900 mt-1">{eligible}</div>
            <div className="text-[10px] text-slate-500 mt-0.5">Legend academies with ≥1 view (7d)</div>
          </div>
          <div className="bg-white rounded-xl border border-emerald-200 p-3.5"
            data-testid="phase167-stat-week">
            <div className="text-[10px] font-black uppercase tracking-widest text-emerald-700 flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3" /> Sent This Week
            </div>
            <div className="text-2xl font-extrabold text-slate-900 mt-1">{sentWeek}</div>
            <div className="text-[10px] text-slate-500 mt-0.5">Rolling 7-day audit</div>
          </div>
          <div className="bg-white rounded-xl border border-emerald-200 p-3.5"
            data-testid="phase167-stat-total">
            <div className="text-[10px] font-black uppercase tracking-widest text-emerald-700 flex items-center gap-1">
              <Mail className="h-3 w-3" /> All-Time Delivered
            </div>
            <div className="text-2xl font-extrabold text-slate-900 mt-1">{total}</div>
            <div className="text-[10px] text-slate-500 mt-0.5">Total successful sends</div>
          </div>
          <div className="bg-white rounded-xl border border-rose-200 p-3.5"
            data-testid="phase167-stat-failed">
            <div className="text-[10px] font-black uppercase tracking-widest text-rose-700 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" /> Failures
            </div>
            <div className="text-2xl font-extrabold text-slate-900 mt-1">{failed}</div>
            <div className="text-[10px] text-slate-500 mt-0.5">All-time failed dispatches</div>
          </div>
        </div>

        {/* Schedule + actions strip */}
        <div className="flex flex-wrap items-center justify-between gap-4 bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Switch
                checked={enabled}
                onCheckedChange={toggleSchedule}
                data-testid="phase167-toggle-cron"
              />
              <span className="text-sm font-semibold text-slate-700">
                Weekly cron
                <span className="text-[11px] text-slate-400 ml-1.5">
                  {enabled ? 'ON' : 'OFF'}
                </span>
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-3.5 w-3.5 text-slate-400" />
              <Select
                value={String(schedule?.weekday ?? 0)}
                onValueChange={(v) => setScheduleField('weekday', Number(v))}
              >
                <SelectTrigger className="h-8 w-[110px] text-xs" data-testid="phase167-weekday">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {WEEKDAYS.map((w, i) => (
                    <SelectItem key={w} value={String(i)}>{w}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={String(schedule?.hour_ist ?? 9)}
                onValueChange={(v) => setScheduleField('hour_ist', Number(v))}
              >
                <SelectTrigger className="h-8 w-[100px] text-xs" data-testid="phase167-hour">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 24 }, (_, h) => (
                    <SelectItem key={h} value={String(h)}>
                      {String(h).padStart(2, '0')}:00 IST
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <span className="text-[11px] text-slate-500">
              Next window: <strong className="text-slate-800">{schedule?.next_window_label || '—'}</strong>
              {schedule?.last_fired_at && (
                <span className="ml-2 text-slate-400">
                  · Last fired {new Date(schedule.last_fired_at).toLocaleString('en-IN')}
                </span>
              )}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline" size="sm" onClick={openPreview}
              className="h-8 text-xs"
              data-testid="phase167-preview-btn"
            >
              <Eye className="h-3 w-3 mr-1" /> Preview Digest
            </Button>
            <Button
              size="sm"
              onClick={() => setShowRun(true)}
              className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700"
              data-testid="phase167-run-btn"
              disabled={eligible === 0}
            >
              <Send className="h-3 w-3 mr-1" />
              Run Now ({eligible})
            </Button>
          </div>
        </div>

        {/* Last campaign trace */}
        {last && (
          <div className="bg-slate-50 border border-slate-200 rounded-lg px-3.5 py-2.5 text-[11px] text-slate-600 flex flex-wrap items-center gap-x-3 gap-y-1"
            data-testid="phase167-last-campaign">
            <span>
              Last campaign:{' '}
              <strong className="text-slate-800">{last.campaign_tag}</strong>
            </span>
            <span>·</span>
            <span>
              Target <strong>{last.target_count}</strong>
            </span>
            <span>·</span>
            <span>
              Sent <strong className="text-emerald-700">{last.stats?.sent ?? 0}</strong>
              {' · '}
              Failed <strong className="text-rose-700">{last.stats?.failed ?? 0}</strong>
              {' · '}
              Skipped <strong className="text-slate-500">{(last.stats?.skipped_no_owner ?? 0) + (last.stats?.skipped_no_views ?? 0)}</strong>
            </span>
            <span>·</span>
            <span>
              {last.completed_at ? `Completed ${new Date(last.completed_at).toLocaleString('en-IN')}` : 'In progress'}
            </span>
          </div>
        )}

        {/* Phase 168.b — per-digest engagement audit + Phase 168.c records */}
        {audit?.summary && (audit.rows?.length || 0) > 0 && (
          <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3"
            data-testid="phase168b-audit-section">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-700 flex items-center gap-1.5">
                <MailOpen className="h-3 w-3" /> Recent digest engagement
                <span className="text-slate-400 normal-case tracking-normal font-normal">· Phase 168.b</span>
              </div>
              <div className="flex items-center gap-2 text-[10px]">
                <span className="text-emerald-700 font-bold">{audit.summary.delivered} delivered</span>
                <span className="text-blue-700 font-bold">{audit.summary.opened} opened</span>
                <span className="text-violet-700 font-bold">{audit.summary.clicked} clicked</span>
                {audit.summary.new_records > 0 && (
                  <span className="text-amber-700 font-bold flex items-center gap-0.5">
                    <Trophy className="h-3 w-3" /> {audit.summary.new_records} record{audit.summary.new_records === 1 ? '' : 's'}
                  </span>
                )}
              </div>
            </div>
            <div className="divide-y divide-slate-100"
              data-testid="phase168b-audit-rows">
              {audit.rows.slice(0, 5).map((row) => (
                <div key={row.id} className="py-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
                  <span className="font-semibold text-slate-800 min-w-[140px] truncate">
                    {row.academy_name || '—'}
                  </span>
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
                    row.status === 'sent' ? 'bg-emerald-100 text-emerald-700' :
                    row.status === 'failed' ? 'bg-rose-100 text-rose-700' :
                    'bg-slate-100 text-slate-600'
                  }`}>{row.status}</span>
                  <span className="text-slate-500">
                    {row.total_views} views
                  </span>
                  <span className="flex items-center gap-2 text-slate-500 ml-auto">
                    <span title="delivered" className={row.delivered_at ? 'text-emerald-700 font-bold' : 'text-slate-300'}>
                      <CheckCircle2 className="inline h-3 w-3 mr-0.5" />
                      {row.delivered_at ? 'D' : '—'}
                    </span>
                    <span title="opened" className={row.first_opened_at ? 'text-blue-700 font-bold' : 'text-slate-300'}>
                      <MailOpen className="inline h-3 w-3 mr-0.5" />
                      {row.open_count || (row.first_opened_at ? 1 : 0) || '—'}
                    </span>
                    <span title="clicked" className={row.first_clicked_at ? 'text-violet-700 font-bold' : 'text-slate-300'}>
                      <MousePointerClick className="inline h-3 w-3 mr-0.5" />
                      {row.click_count || (row.first_clicked_at ? 1 : 0) || '—'}
                    </span>
                    {row.personal_best?.is_new_record && (
                      <Trophy className="h-3 w-3 text-amber-600" title="New personal record" />
                    )}
                  </span>
                </div>
              ))}
            </div>
            {audit.rows.length === 0 && (
              <p className="text-[11px] text-slate-400">
                No digests sent yet — fire one with the Run Now button above.
              </p>
            )}
          </div>
        )}
      </CardContent>

      {/* Preview modal */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
          data-testid="phase167-preview-modal">
          <DialogHeader>
            <DialogTitle>Phase 167 Digest Preview</DialogTitle>
            <DialogDescription>
              {preview ? (
                <>
                  <strong>{preview.academy_name}</strong> ·{' '}
                  <span className="text-emerald-700 font-semibold">{preview.tier}</span> ·{' '}
                  {preview.owner_email || 'no owner email'}
                </>
              ) : 'Loading email body…'}
            </DialogDescription>
          </DialogHeader>
          {previewLoading ? (
            <div className="flex items-center justify-center py-16 text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Rendering…
            </div>
          ) : preview ? (
            <div className="space-y-3 flex-1 overflow-hidden flex flex-col">
              <div className="bg-slate-50 border border-slate-200 rounded p-2 text-xs">
                <strong>Subject:</strong> {preview.subject}
              </div>
              <iframe
                title="Phase 167 digest preview"
                srcDoc={preview.html}
                className="flex-1 w-full bg-white border border-slate-200 rounded-md"
                style={{ minHeight: 520 }}
                data-testid="phase167-preview-iframe"
              />
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Run modal */}
      <Dialog open={showRun} onOpenChange={setShowRun}>
        <DialogContent className="max-w-md" data-testid="phase167-run-modal">
          <DialogHeader>
            <DialogTitle>Run Phase 167 Digest Now</DialogTitle>
            <DialogDescription>
              {eligible} eligible Legend-tier academ{eligible === 1 ? 'y' : 'ies'} will receive
              "Your Week in Scouting" right now.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="phase167-override" className="text-xs font-semibold">
                Override recipient (optional — sends every academy's digest to this email instead)
              </Label>
              <Input
                id="phase167-override"
                type="email" placeholder="you@out-play.in (for end-to-end inbox test)"
                value={runOverride}
                onChange={(e) => setRunOverride(e.target.value)}
                className="mt-1"
                data-testid="phase167-override-input"
              />
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button
                variant="outline" disabled={running}
                onClick={() => runNow(true)}
                data-testid="phase167-run-dry"
              >
                {running ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
                Dry Run
              </Button>
              <Button
                disabled={running}
                onClick={() => runNow(false)}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="phase167-run-live"
              >
                {running ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Send className="h-3.5 w-3.5 mr-1" />}
                Send Live ({eligible})
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
